import { StaticJsonRpcProvider } from '@ethersproject/providers'
import { formatUnits } from '@ethersproject/units'
import {
  ROBINHOOD_CURATED_DECIMALS,
  ROBINHOOD_TOKEN_LIST,
  ROBINHOOD_TOKEN_METADATA,
} from 'constants/robinhoodTokens'
import { useEffect, useMemo, useState } from 'react'
import { UniverseChainId } from 'uniswap/src/types/chains'
import { fetchRobinhoodDiscovery, type DiscoveredToken } from 'utils/robinhoodPortfolio/discovery'
import { readBalances, readDecimals } from 'utils/robinhoodPortfolio/balances'

export interface RobinhoodHolding {
  /** Token contract address, or the native sentinel below. */
  address: string
  isNative: boolean
  symbol: string
  name: string
  decimals: number
  logoURI?: string
  /** Raw on-chain balance (authoritative). */
  balance: bigint
  /** Human display quantity (for sort/label only — never a transaction input). */
  quantity: number
  /** Display USD value (untrusted price × quantity), if a price was discovered. */
  usdValue?: number
}

export const ROBINHOOD_NATIVE_SENTINEL = 'NATIVE'

interface PortfolioState {
  holdings: RobinhoodHolding[]
  loading: boolean
  /** True when Blockscout discovery failed and we fell back to the curated list only — the displayed
   * set is NOT exhaustive, and the UI must say so. */
  discoveryLimited: boolean
  /** Lowercased account these holdings were loaded for. Results are consumed ONLY when this matches
   * the currently-requested account, so wallet A's holdings never render for wallet B. */
  account?: string
}

const EMPTY: PortfolioState = { holdings: [], loading: false, discoveryLimited: false, account: undefined }

/** Curated list as discovery entries — the fallback token set when Blockscout is unavailable. */
const CURATED_AS_DISCOVERED: DiscoveredToken[] = ROBINHOOD_TOKEN_LIST.map((t) => ({
  address: t.address,
  symbol: t.symbol,
  name: t.name,
  decimals: t.decimals,
  logoURI: t.logoURI,
}))

/**
 * Robinhood Chain (4663) wallet holdings for the portfolio tray. HYBRID + trust-minimized:
 *   - DISCOVERY of which token contracts the wallet holds comes from the chain's own Blockscout
 *     explorer (never Uniswap). On failure we fall back to the curated token list and flag
 *     `discoveryLimited`.
 *   - BALANCES are read on-chain via Multicall3 (authoritative); Blockscout balances are never used.
 *   - Decimals prefer the curated map, else are verified on-chain. Logos/symbols/prices are untrusted
 *     display data. Zero balances are hidden; a single broken token can't break the batch.
 */
// Dedicated read-only provider on the public 4663 RPC. Deliberately NOT the wagmi/wallet provider:
// holdings must load for any connected wallet address regardless of which chain the wallet is on
// (the connector-backed provider only resolves 4663 when the wallet itself is on 4663, which gated
// the tray to Robinhood-selected sessions). Static: skips eth_chainId detection round-trips.
const ROBINHOOD_READ_PROVIDER = new StaticJsonRpcProvider(
  'https://rpc.mainnet.chain.robinhood.com',
  UniverseChainId.Robinhood,
)

export function useRobinhoodPortfolio(account?: string): PortfolioState {
  const provider = ROBINHOOD_READ_PROVIDER
  const [state, setState] = useState<PortfolioState>(EMPTY)

  useEffect(() => {
    if (!account || !provider) {
      setState(EMPTY)
      return
    }
    const key = account.toLowerCase()
    let cancelled = false
    // Clear the previous account's holdings on switch (don't preserve them under a spinner) and stamp
    // the identity so the render-time guard below only surfaces results once they match this account.
    setState({ holdings: [], loading: true, discoveryLimited: false, account: key })

    ;(async () => {
      // 1. Discover the token set (Blockscout → curated fallback).
      let discovered: DiscoveredToken[]
      let discoveryLimited = false
      try {
        discovered = await fetchRobinhoodDiscovery(account)
        // Union curated tokens so well-known assets always appear if held, even if the indexer misses them.
        const seen = new Set(discovered.map((d) => d.address.toLowerCase()))
        discovered = [...discovered, ...CURATED_AS_DISCOVERED.filter((c) => !seen.has(c.address.toLowerCase()))]
      } catch {
        discovered = CURATED_AS_DISCOVERED
        discoveryLimited = true
      }
      if (cancelled) return

      const addresses = discovered.map((d) => d.address)

      // 2. Resolve decimals: curated map is trusted; every other token is verified on-chain.
      // Blockscout's reported decimals are untrusted display data (a wrong value misrenders
      // balance/USD by orders of magnitude), so they are never used — read decimals() directly.
      const needDecimals = addresses.filter(
        (a) => ROBINHOOD_CURATED_DECIMALS[a.toLowerCase()] === undefined,
      )
      let onchainDecimals = new Map<string, number>()
      // 3. Read balances (authoritative) + missing decimals, on-chain.
      let balances = new Map<string, bigint>()
      let nativeBalance = 0n
      try {
        ;[balances, onchainDecimals, nativeBalance] = await Promise.all([
          readBalances(provider, account, addresses),
          needDecimals.length ? readDecimals(provider, needDecimals) : Promise.resolve(new Map<string, number>()),
          provider.getBalance(account).then((b) => BigInt(b.toString())),
        ])
      } catch {
        // On-chain read failure → nothing trustworthy to show.
        if (!cancelled) setState({ holdings: [], loading: false, discoveryLimited, account: key })
        return
      }
      if (cancelled) return

      const holdings: RobinhoodHolding[] = []

      // Native RH ETH.
      if (nativeBalance > 0n) {
        holdings.push(buildHolding(ROBINHOOD_NATIVE_SENTINEL, true, 'ETH', 'Ether', 18, undefined, nativeBalance, undefined))
      }

      for (const d of discovered) {
        const key = d.address.toLowerCase()
        const balance = balances.get(key)
        if (balance === undefined || balance <= 0n) continue // hide zero / unreadable

        // Curated map first, else the on-chain decimals() read. Blockscout's d.decimals is
        // deliberately not consulted — a non-curated token with no readable on-chain decimals
        // is dropped rather than rendered with an untrusted precision.
        const decimals = ROBINHOOD_CURATED_DECIMALS[key] ?? onchainDecimals.get(key)
        if (decimals === undefined) continue // can't safely display without decimals

        const curated = ROBINHOOD_TOKEN_METADATA[key]
        const symbol = curated?.symbol ?? d.symbol ?? shortAddr(d.address)
        const name = curated?.name ?? d.name ?? symbol
        // Only curated (fixed, trusted) tokens get a real logo. Non-curated tokens render a local
        // blank — we never load an attacker-controlled discovery URL (wallet-correlation leak).
        const logoURI = curated?.logoURI
        holdings.push(buildHolding(d.address, false, symbol, name, decimals, logoURI, balance, d.priceUsd))
      }

      holdings.sort((a, b) => (b.usdValue ?? 0) - (a.usdValue ?? 0) || b.quantity - a.quantity)
      if (!cancelled) setState({ holdings, loading: false, discoveryLimited, account: key })
    })()

    return () => {
      cancelled = true
    }
  }, [account, provider])

  return selectPortfolioView(state, account, Boolean(provider))
}

/**
 * Synchronously binds a loaded {@link PortfolioState} to the CURRENTLY-requested account. Until the
 * effect has committed a result stamped for this exact account, it reports loading with empty holdings.
 * This covers both the first render (state begins as EMPTY before the effect runs) and the A→B switch
 * frame (state still holds A's result), so a Robinhood-only wallet never flashes empty and wallet B
 * never sees wallet A's tokens. Pure + exported for tests.
 */
export function selectPortfolioView(
  state: PortfolioState,
  account: string | undefined,
  providerReady: boolean,
): PortfolioState {
  if (!account || !providerReady) {
    return EMPTY
  }
  const requestedKey = account.toLowerCase()
  if (state.account !== requestedKey) {
    return { holdings: [], loading: true, discoveryLimited: false, account: requestedKey }
  }
  return state
}

function buildHolding(
  address: string,
  isNative: boolean,
  symbol: string,
  name: string,
  decimals: number,
  logoURI: string | undefined,
  balance: bigint,
  priceUsd: number | undefined,
): RobinhoodHolding {
  const quantity = Number(formatUnits(balance.toString(), decimals))
  const usdValue = priceUsd !== undefined && Number.isFinite(quantity) ? priceUsd * quantity : undefined
  return { address, isNative, symbol, name, decimals, logoURI, balance, quantity, usdValue }
}

function shortAddr(a: string): string {
  return `${a.slice(0, 6)}…${a.slice(-4)}`
}

/** Convenience: total displayed USD value (best-effort; excludes tokens without a price). */
export function useRobinhoodPortfolioUsd(account?: string): number {
  const { holdings } = useRobinhoodPortfolio(account)
  return useMemo(() => holdings.reduce((sum, h) => sum + (h.usdValue ?? 0), 0), [holdings])
}
